import type { WayfindingHere, WayfindingPlay, WayfindingRecent, WayfindingStep } from './wayfinding.models';

const SKIP_ROUTES = new Set(['', '/', '/login', '/dashboard']);

export function normalizeRoute(url: string): string {
  const path = (url ?? '').split('?')[0].split('#')[0].trim();
  if (!path) {
    return '';
  }

  const withSlash = path.startsWith('/') ? path : `/${path}`;
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : withSlash;
}

export function isWorkScreen(route: string): boolean {
  const path = normalizeRoute(route);
  return !SKIP_ROUTES.has(path);
}

export function routeContains(current: string, candidate: string): boolean {
  const a = normalizeRoute(current);
  const b = normalizeRoute(candidate);
  if (!a || !b) {
    return false;
  }

  return a === b || a.startsWith(`${b}/`);
}

export function playForLocation<T extends WayfindingPlay>(route: string, plays: T[]): T | null {
  const current = normalizeRoute(route);
  if (!isWorkScreen(current)) {
    return null;
  }

  const ranked = plays
    .map(play => {
      const match = matchingStep(play, current);
      if (!match) {
        return null;
      }

      const remaining = play.steps.slice(match.index + 1);
      const curated = !(play.id ?? '').startsWith('play-crawled-');
      return {
        play,
        stepLength: normalizeRoute(match.step.route).length,
        remaining: remaining.filter(step => !step.optional).length,
        curated
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .sort((a, b) =>
      Number(b.curated) - Number(a.curated)
      || b.stepLength - a.stepLength
      || b.remaining - a.remaining
      || a.play.steps.length - b.play.steps.length
    );

  return ranked[0]?.play ?? null;
}

export function remainingSteps(play: WayfindingPlay, currentRoute: string): WayfindingStep[] {
  const match = matchingStep(play, currentRoute);
  if (!match) {
    return play.steps;
  }

  return play.steps.slice(match.index + 1);
}

export function continuePlayFrom<T extends WayfindingPlay>(play: T, currentRoute: string): T {
  const remaining = remainingSteps(play, currentRoute);
  if (remaining.length === 0) {
    const match = matchingStep(play, currentRoute);
    return {
      ...play,
      steps: match ? [match.step, ...play.steps.filter(step => step.optional && step !== match.step)] : play.steps
    };
  }

  return { ...play, steps: remaining };
}

export function shouldContinuePlay(
  query: string,
  currentRoute: string,
  play: WayfindingPlay | null,
  composedRoutes: string[],
  translate: (key: string) => string
): boolean {
  if (!play) {
    return false;
  }

  const remaining = remainingSteps(play, currentRoute);
  if (remaining.length === 0) {
    return false;
  }

  const remainingMatch = remaining.some(step => haystackMatchesQuery(stepHaystack(step, translate), query));
  if (!remainingMatch) {
    return false;
  }

  const hereRoutes = play.steps.map(step => normalizeRoute(step.route));
  const outside = composedRoutes.filter(route =>
    !hereRoutes.some(here => routeContains(route, here) || routeContains(here, route))
  );

  if (outside.length >= 2) {
    return false;
  }

  if (composedRoutes.length >= 3 && outside.length >= 1) {
    return false;
  }

  return true;
}

export function describeHere(
  route: string,
  items: Array<{ route: string; label: string }>,
  translate: (key: string) => string,
  play?: WayfindingPlay | null
): WayfindingHere | null {
  const current = normalizeRoute(route);
  if (!isWorkScreen(current)) {
    return null;
  }

  const match = items
    .filter(item => routeContains(current, item.route))
    .sort((a, b) => normalizeRoute(b.route).length - normalizeRoute(a.route).length)[0];

  return {
    route: match ? normalizeRoute(match.route) : current,
    label: match ? translate(match.label) : fallbackLabel(current),
    playId: play?.id,
    playTitleKey: play?.titleKey
  };
}

export function contextBoost(route: string, currentRoute: string, recents: WayfindingRecent[]): number {
  if (routeContains(currentRoute, route) || routeContains(route, currentRoute)) {
    return 40;
  }

  const index = recents.findIndex(item => routeContains(item.route, route) || routeContains(route, item.route));
  if (index < 0) {
    return 0;
  }

  return Math.max(8, 24 - index * 2);
}

function matchingStep(play: WayfindingPlay, currentRoute: string): { step: WayfindingStep; index: number } | null {
  let best: { step: WayfindingStep; index: number; length: number } | null = null;
  play.steps.forEach((step, index) => {
    if (!routeContains(currentRoute, step.route)) {
      return;
    }

    const length = normalizeRoute(step.route).length;
    if (!best || length > best.length) {
      best = { step, index, length };
    }
  });

  return best;
}

function stepHaystack(step: WayfindingStep, translate: (key: string) => string): string {
  return [
    translate(step.titleKey),
    translate(step.whyKey),
    step.route.replaceAll('/', ' ').replaceAll('-', ' ')
  ].join(' ');
}

function haystackMatchesQuery(haystack: string, query: string): boolean {
  const original = fold(query);
  const hay = fold(haystack);
  if (!original) {
    return true;
  }

  if (!hay) {
    return false;
  }

  if (hay.includes(original)) {
    return true;
  }

  if (original.split(' ').filter(token => token.length > 1).some(token => hay.includes(token))) {
    return true;
  }

  return original.includes('انتشار') && (hay.includes('publish') || hay.includes('publishing'));
}

function fold(value: string): string {
  return (value ?? '')
    .toLowerCase()
    .replaceAll('\u200c', '')
    .replaceAll('ي', 'ی')
    .replaceAll('ك', 'ک')
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function fallbackLabel(route: string): string {
  const tail = normalizeRoute(route).split('/').filter(Boolean).slice(-2).join(' / ');
  return tail || route;
}
